<template>
  <div class="h-full flex">
    <div class="w-full relative bg-white">
      <div class="h-full w-full flex items-start">
        <!-- Finalize / AI suggestion button -->
        <FloatingMenu v-if="isAiSuggested" placement="bottom" :offset="[0, 8]" class="h-full">
          <template #activator="{ show }">
            <button
              class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-3 text-gray-800 rounded-md p-2"
              :class="{
                'sticky top-27': expansionPanel?.isShown.value,
                'border border-gray-800': aiSuggestedNotReported,
                'border border-green-500': !aiSuggestedNotReported && isAiSuggested,
              }"
              :title="finalizeButtonTitle"
              @click="show"
            >
              <MinusIcon v-if="isAnswerEmpty" class="w-4" />
              <CheckIcon v-else class="w-4" />
              <SparklesIcon class="w-4" />
            </button>
          </template>
          <template #content="{ hide }">
            <div class="flex flex-col bg-white rounded-lg shadow-lg py-2">
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/accept-suggestion font-medium text-gray-900 flex items-center gap-2 disabled:opacity-50"
                :disabled="!!loadingAction"
                @click="acceptAiSuggestion(hide)"
              >
                <i-material-symbols-progress-activity
                  v-if="loadingAction === 'accept'"
                  class="size-5 animate-spin"
                />
                <CheckIcon v-else class="size-5 group-hover/accept-suggestion:text-green-600" />
                Accept AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/edit-suggestion font-medium text-gray-900 flex items-center gap-2 disabled:opacity-50"
                :disabled="!!loadingAction"
                @click="editAiSuggestion(hide)"
              >
                <i-material-symbols-progress-activity
                  v-if="loadingAction === 'edit'"
                  class="size-4 mr-1 animate-spin"
                />
                <PencilIcon v-else class="size-4 mr-1 group-hover/edit-suggestion:text-blue-600" />
                Edit AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 group/reject-suggestion font-medium text-gray-900 flex items-center gap-2 disabled:opacity-50"
                :disabled="!!loadingAction"
                @click="rejectAiSuggestion(hide)"
              >
                <i-material-symbols-progress-activity
                  v-if="loadingAction === 'reject'"
                  class="size-5 animate-spin"
                />
                <XMarkIcon v-else class="size-5 group-hover/reject-suggestion:text-red-600" />
                Reject AI suggestion
              </button>
            </div>
          </template>
        </FloatingMenu>
        <button
          v-else
          class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-2 rounded-md text-gray-800 p-2"
          :class="{
            'sticky top-27': expansionPanel?.isShown.value,
            'bg-gray-800 text-white': isAnswerEmpty && isFinalized,
            'bg-green-500 text-white': !isAnswerEmpty && isFinalized,
          }"
          :disabled="loadingAction === 'finalize'"
          :title="finalizeButtonTitle"
          @click="toggleReviewItemSynthesisAttributeFinalization"
        >
          <i-material-symbols-progress-activity
            v-if="loadingAction === 'finalize'"
            class="w-4 animate-spin"
          />
          <template v-else>
            <MinusIcon v-if="isAnswerEmpty" class="w-4" />
            <CheckIcon v-else class="w-4" />
          </template>
        </button>

        <!-- Thumbnail area (always visible) -->
        <div class="flex-1 px-2 py-2">
          <div
            v-if="isAnswerEmpty && isFinalized"
            class="flex items-center p-2 border border-black rounded-lg text-sm text-gray-700 bg-gray-100 w-fit"
          >
            Not reported
          </div>
          <StudyDesignThumbnail
            v-else-if="!isAnswerEmpty"
            :populations="normalizedStudyDesignValue?.populations ?? []"
            :arms="normalizedStudyDesignValue?.arms ?? []"
            :periods="normalizedStudyDesignValue?.periods ?? []"
            :intervention-definitions="normalizedStudyDesignValue?.interventionDefinitions ?? []"
            class="h-48 cursor-pointer"
            @click="isFullscreen = true"
          />
          <button
            v-else
            class="flex items-center gap-2 text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg px-3 py-2 hover:border-slate-400 hover:text-slate-700 transition-colors"
            @click="isFullscreen = true"
          >
            Edit study design
          </button>
        </div>
      </div>

      <div class="absolute bottom-2 right-2 flex items-center gap-1">
        <EquivalentExtractionCopyStudyDesign
          v-if="showEquivalentCopy && !review.isReviewReadonly.value"
          :study="study"
          @copy-study-design="handleStudyDesignCopy"
        />
        <StudyQA
          v-if="!review.isReviewReadonly.value && !isFinalized"
          v-model:is-generating-answer="isGeneratingAnswer"
          :study="study"
          :attribute-structure="attributeStructure"
          :query="attributeStructure.question ?? ''"
          :is-review-readonly="review.isReviewReadonly.value"
        />
      </div>
    </div>

    <!-- Fullscreen builder overlay -->
    <Teleport to="body">
      <div v-if="isFullscreen" class="fixed inset-0 z-100 flex flex-col bg-white">
        <StudyDesignEditor
          :study="study"
          :attribute-structure="attributeStructure"
          :disabled="isFinalized || isAiSuggested || isReviewReadOnly"
          class="min-h-0"
        >
          <template #toolbar-start>
            <button
              class="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors px-2 py-1 rounded hover:bg-slate-100"
              @click="isFullscreen = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clip-rule="evenodd"
                />
              </svg>
              Back
            </button>
          </template>
        </StudyDesignEditor>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import StudyQA from "./StudyQA.vue";
import StudyDesignEditor from "./StudyDesignEditor.vue";
import { StudyDesignThumbnail } from "visual-study-design-builder";
import { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import type { StudyDesignValue } from "@core/domain/types/attributeValue.type";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useSnackbar from "@app/composables/use-snackbar";
import { HttpException } from "@core/exceptions/http.exception";
import { SnackbarState } from "@app/types";
import { ReviewLockState } from "@core/domain/types/reviewLockState.type";
import { errorMessage } from "@app/utils/error-message";
import { computed, ref } from "vue";
import EquivalentExtractionCopyStudyDesign from "./EquivalentExtractionCopyStudyDesign.vue";

import { SparklesIcon, CheckIcon, MinusIcon, PencilIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";

const snackbar = useSnackbar();
const loadingAction = ref<"finalize" | "accept" | "reject" | "edit" | null>(null);
const isFullscreen = ref(false);

const props = withDefaults(
  defineProps<{
    attributeStructure: AttributeStructure;
    study: ReviewItem;
    isExpanded: boolean;
    isReviewReadOnly?: boolean;
    expansionPanel?: any;
    showEquivalentCopy?: boolean;
  }>(),
  {
    isReviewReadOnly: false,
    showEquivalentCopy: false,
  },
);

const isGeneratingAnswer = ref(false);
const review = injectStrict(ReviewKey);

const currentAttribute = computed(() =>
  props.study.synthesis?.attributes?.find(
    (attr) => attr.attributeStructureId === props.attributeStructure?.id,
  ),
);

const studyDesignValue = computed(
  () => currentAttribute.value?.value as StudyDesignValue | undefined,
);

const normalizedStudyDesignValue = computed(() => studyDesignValue.value);

const isAnswerEmpty = computed(() => {
  const value = studyDesignValue.value;
  return !value?.populations?.length && !value?.arms?.length && !value?.periods?.length;
});

const isFinalized = computed(() => currentAttribute.value?.isFinalized ?? false);
const isAiSuggested = computed(() => currentAttribute.value?.isAiSuggested ?? false);
const aiSuggestedNotReported = computed(
  () => currentAttribute.value?.aiSuggestedNotReported ?? false,
);

async function toggleReviewItemSynthesisAttributeFinalization() {
  if (review.entity.value.plan?.lockState === ReviewLockState.UNLOCKED) {
    return;
  }
  loadingAction.value = "finalize";
  try {
    if (isFinalized.value) {
      await unfinalizeReviewItemSynthesisAttribute();
    } else {
      await finalizeReviewItemSynthesisAttribute();
    }
    loadingAction.value = null;
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loadingAction.value = null;
  }
}

async function acceptAiSuggestion(hide: () => void) {
  loadingAction.value = "accept";
  try {
    await review.editAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    await finalizeReviewItemSynthesisAttribute();
    loadingAction.value = null;
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loadingAction.value = null;
  }
}

async function rejectAiSuggestion(hide: () => void) {
  loadingAction.value = "reject";
  try {
    await review.clearAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    loadingAction.value = null;
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loadingAction.value = null;
  }
}

async function editAiSuggestion(hide: () => void) {
  loadingAction.value = "edit";
  try {
    await review.editAttributeAiSuggestion(props.study.id, props.attributeStructure.id);
    loadingAction.value = null;
    hide();
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loadingAction.value = null;
  }
}

async function finalizeReviewItemSynthesisAttribute() {
  try {
    await review.finalizeReviewItemSynthesisAttribute(props.study.id, props.attributeStructure.id);
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  }
}

async function unfinalizeReviewItemSynthesisAttribute() {
  try {
    await review.unfinalizeReviewItemSynthesisAttribute(
      props.study.id,
      props.attributeStructure.id,
    );
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  }
}

async function handleStudyDesignCopy(value: StudyDesignValue) {
  try {
    await review.saveStudyDesignAttributeValue(props.study.id, props.attributeStructure.id, value);
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
  }
}

const finalizeButtonTitle = computed(() => {
  if (isFinalized.value) return "Edit extraction";
  return isAnswerEmpty.value ? "Mark as not reported" : "Confirm extraction";
});
</script>
